<template>
  <div>
    <v-data-table
      :headers="discountDataTableHeaders"
      :items="discountList"
      class="elevation-1"
      :no-data-text="$t('common_no_data')"
      :no-results-text="$t('common_no_results')"
      :loading="isLoadingDiscount"
      :loading-text="$t('common_get_data')"
      :footer-props="{
        'items-per-page-options': [30, 50, 100],
        'items-per-page-text': $t('item_rows_per_page'),
      }"
      :header-props="headerProps"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>{{ $t('discount_title') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-dialog v-model="isShowEditDiscountDialog" fullscreen persistent>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary" dark class="mb-2 ml-8" v-bind="attrs" v-on="on">
                {{ $t('common_new') }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="text-h5">{{ discountDialogTitle }}</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <ValidationObserver ref="dialogDiscount">
                    <v-row>
                      <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                        <customer-autocomplete
                          :fatherEditedCustomer="editedDiscount"
                          :editedCustomerIndex="-1"
                          :customerName="'quotation_customer_number'"
                          @setCustomer="setCustomer"
                          v-if="editedDiscountIndex === -1"
                          refName="customerAutocomplete"
                          @keyupEnter="focusNextInput"
                        />
                        <ValidationProvider
                          :name="$t('quotation_customer_number')"
                          rules="required"
                          v-slot="{ errors }"
                          v-else
                        >
                          <v-text-field
                            v-model="editedDiscount.CustomerNumber"
                            :label="$t('quotation_customer_number')"
                            disabled
                            :error-messages="errors[0]"
                          >
                            <template v-slot:label>
                              <span class="red--text">
                                <strong>* </strong>
                              </span>
                              {{ $t('quotation_customer_number') }}
                            </template>
                          </v-text-field>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                        <ValidationProvider
                          :name="$t('quotation_shortname')"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            v-model="editedDiscount.CustomerName"
                            :label="$t('quotation_shortname')"
                            disabled
                            :error-messages="errors[0]"
                          >
                            <template v-slot:label>
                              <span class="red--text">
                                <strong>* </strong>
                              </span>
                              {{ $t('quotation_shortname') }}
                            </template>
                          </v-text-field>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                        <ValidationProvider
                          :name="$t('quotation_full_name')"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            v-model="editedDiscount.CustomerFullName"
                            :label="$t('quotation_full_name')"
                            disabled
                            :error-messages="errors[0]"
                          >
                            <template v-slot:label>
                              <span class="red--text">
                                <strong>* </strong>
                              </span>
                              {{ $t('quotation_full_name') }}
                            </template>
                          </v-text-field>
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                        <ValidationProvider
                          :name="$t('quotation_license_plate')"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            v-model="editedDiscount.LicensePlate"
                            :label="$t('quotation_license_plate')"
                            :error-messages="errors[0]"
                            required
                            clearable
                            :disabled="isAllTextFieldDisable"
                            @keyup.enter="focusNextInput"
                          >
                            <template v-slot:label>
                              <span class="red--text">
                                <strong>* </strong>
                              </span>
                              {{ $t('quotation_license_plate') }}
                            </template>
                          </v-text-field>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                        <ValidationProvider
                          :name="$t('discount_month_total')"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            v-model="editedDiscount.MonthTotal"
                            :label="$t('discount_month_total')"
                            :error-messages="errors[0]"
                            required
                            clearable
                            :disabled="isAllTextFieldDisable"
                            @keyup.enter="focusNextInput"
                          >
                            <template v-slot:label>
                              <span class="red--text">
                                <strong>* </strong>
                              </span>
                              {{ $t('discount_month_total') }}
                            </template>
                          </v-text-field>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                        <ValidationProvider
                          :name="$t('quotation_item_rent_warranty_period')"
                          :rules="{ required: true, regex: /^[0-9]+$/ }"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            v-model="editedDiscount.WarrantyPeriod"
                            :label="$t('quotation_item_rent_warranty_period')"
                            :error-messages="errors[0]"
                            required
                            clearable
                            :disabled="isAllTextFieldDisable"
                            @keyup.enter="focusNextInput"
                          >
                            <template v-slot:label>
                              <span class="red--text">
                                <strong>* </strong>
                              </span>
                              {{ $t('quotation_item_rent_warranty_period') }}
                            </template>
                          </v-text-field>
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                  </ValidationObserver>
                </v-container>
              </v-card-text>

              <v-data-table
                :headers="discountItemDataTableHeaders"
                :items="editedDiscount.DiscountItems"
                :search="searchDiscountItem"
                class="elevation-1  mt-5 mx-10"
                :no-data-text="$t('common_no_data')"
                :no-results-text="$t('common_no_results')"
                :footer-props="{
                  'items-per-page-options': [30, 50, 100],
                  'items-per-page-text': $t('item_rows_per_page'),
                }"
                :header-props="headerProps"
              >
                <template v-slot:top>
                  <v-toolbar flat>
                    <v-toolbar-title></v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-text-field
                      v-model="searchDiscountItem"
                      append-icon="mdi-magnify"
                      :label="$t('customer_repair_item_search_label')"
                      single-line
                      hide-details
                      clearable
                    ></v-text-field>
                    <v-dialog v-model="isShowEditDiscountItemDialog" persistent>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          color="primary"
                          dark
                          class="mb-2 ml-8"
                          v-bind="attrs"
                          v-on="on"
                          :disabled="isAllTextFieldDisable"
                        >
                          {{ $t('common_new') }}
                        </v-btn>
                      </template>
                      <v-card>
                        <v-card-title>
                          <span class="text-h5">{{ discountItemDialogTitle }}</span>
                        </v-card-title>
                        <v-card-text>
                          <v-container>
                            <ValidationObserver ref="discountItem">
                              <v-row>
                                <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                                  <ValidationProvider
                                    :name="$t('discount_item_type')"
                                    rules="required"
                                    v-slot="{ errors }"
                                  >
                                    <v-autocomplete
                                      v-model="editedDiscountItem.DiscountType"
                                      :items="allDiscountType"
                                      item-text="Name"
                                      item-value="Type"
                                      :disabled="isAllTextFieldDisable"
                                      :loading-text="$t('common_get_data')"
                                      :no-data-text="$t('common_no_data')"
                                      @keyup.enter="setDiscountTypeAutocomplete"
                                      ref="discountTypeAutocomplete"
                                      :error-messages="errors[0]"
                                    >
                                      <template v-slot:label>
                                        <span class="red--text">
                                          <strong>* </strong>
                                        </span>
                                        {{ $t('discount_item_type') }}
                                      </template>
                                    </v-autocomplete>
                                  </ValidationProvider>
                                </v-col>
                                <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                                  <ValidationProvider
                                    :name="$t('discount_item_use_type')"
                                    rules="required"
                                    v-slot="{ errors }"
                                  >
                                    <v-autocomplete
                                      v-model="editedDiscountItem.UseDiscountType"
                                      :items="allDiscountItemType"
                                      item-text="Name"
                                      item-value="Type"
                                      :disabled="isAllTextFieldDisable || lock"
                                      :loading-text="$t('common_get_data')"
                                      :no-data-text="$t('common_no_data')"
                                      @keyup.enter="setUseDiscountTypeAutocomplete"
                                      ref="useDiscountTypeAutocomplete"
                                      :error-messages="errors[0]"
                                    >
                                      <template v-slot:label>
                                        <span class="red--text">
                                          <strong>* </strong>
                                        </span>
                                        {{ $t('discount_item_use_type') }}
                                      </template>
                                    </v-autocomplete>
                                  </ValidationProvider>
                                </v-col>

                                <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                                  <ValidationProvider
                                    :name="$t('discount_item_use_warranty_period')"
                                    rules="required"
                                    v-slot="{ errors }"
                                    v-if="
                                      editedDiscountItem.DiscountType !== 1 &&
                                        editedDiscountItem.UseDiscountType === 1
                                    "
                                  >
                                    <v-text-field
                                      v-model="editedDiscountItem.RepeatWarrantyPeriod"
                                      v-if="
                                        editedDiscountItem.DiscountType !== 1 &&
                                          editedDiscountItem.UseDiscountType === 1
                                      "
                                      @keyup.enter="focusNextInput"
                                      :error-messages="errors[0]"
                                    >
                                      <template v-slot:label>
                                        <span class="red--text">
                                          <strong>* </strong>
                                        </span>
                                        {{ $t('discount_item_use_warranty_period') }}
                                      </template>
                                    </v-text-field>
                                  </ValidationProvider>
                                  <ValidationProvider
                                    :name="$t('discount_item_use_month')"
                                    rules="required"
                                    v-slot="{ errors }"
                                    v-if="editedDiscountItem.UseDiscountType === 2"
                                  >
                                    <v-text-field
                                      v-model="editedDiscountItem.Month"
                                      v-if="editedDiscountItem.UseDiscountType === 2"
                                      @keyup.enter="focusNextInput"
                                      :error-messages="errors[0]"
                                    >
                                      <template v-slot:label>
                                        <span class="red--text">
                                          <strong>* </strong>
                                        </span>
                                        {{ $t('discount_item_use_month') }}
                                      </template>
                                    </v-text-field>
                                  </ValidationProvider>
                                  <ValidationProvider
                                    :name="$t('discount_item_use_warranty_period')"
                                    rules="required"
                                    v-slot="{ errors }"
                                    v-if="
                                      editedDiscountItem.DiscountType === 1 &&
                                        editedDiscountItem.UseDiscountType === 1
                                    "
                                  >
                                    <v-autocomplete
                                      v-model="editedDiscountItem.UseWarrantyPeriods"
                                      :items="allMonth"
                                      multiple
                                      small-chips
                                      :disabled="isAllTextFieldDisable"
                                      :loading-text="$t('common_get_data')"
                                      :no-data-text="$t('common_no_data')"
                                      v-if="
                                        editedDiscountItem.DiscountType === 1 &&
                                          editedDiscountItem.UseDiscountType === 1
                                      "
                                      @keydown.enter="setKeyDownAutocomplete"
                                      @keyup.enter="setKeyUpAutocomplete"
                                      ref="useWarrantyPeriodsAutocomplete"
                                      :error-messages="errors[0]"
                                    >
                                      <template v-slot:label>
                                        <span class="red--text">
                                          <strong>* </strong>
                                        </span>
                                        {{ $t('discount_item_use_warranty_period') }}
                                      </template>
                                    </v-autocomplete>
                                  </ValidationProvider>
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                                  <date-picker-required
                                    :dateName="'month_start_date'"
                                    :isAllTextFieldDisabled="false"
                                    @setDate="setStartTime"
                                    @keyupEnter="focusNextInput"
                                    v-if="editedDiscountItemIndex === -1"
                                  />
                                  <ValidationProvider
                                    :name="$t('month_start_date')"
                                    rules="required"
                                    v-slot="{ errors }"
                                    v-else
                                  >
                                    <v-text-field
                                      v-model="computeStartTime"
                                      required
                                      disabled
                                      :error-messages="errors[0]"
                                    >
                                      <template v-slot:label>
                                        <span class="red--text">
                                          <strong>* </strong>
                                        </span>
                                        {{ $t('month_start_date') }}
                                      </template>
                                    </v-text-field>
                                  </ValidationProvider>
                                </v-col>
                                <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                                  <ValidationProvider
                                    :name="$t('discount_item_give_warranty_period')"
                                    rules="required"
                                    v-slot="{ errors }"
                                    v-if="editedDiscountItem.DiscountType === 1"
                                  >
                                    <v-text-field
                                      v-model="editedDiscountItem.GiveWarrantyPeriod"
                                      v-if="editedDiscountItem.DiscountType === 1"
                                      @keyup.enter="focusNextInput"
                                      :error-messages="errors[0]"
                                    >
                                      <template v-slot:label>
                                        <span class="red--text">
                                          <strong>* </strong>
                                        </span>
                                        {{ $t('discount_item_give_warranty_period') }}
                                      </template>
                                    </v-text-field>
                                  </ValidationProvider>
                                  <ValidationProvider
                                    :name="$t('discount_item_give_money')"
                                    rules="required"
                                    v-slot="{ errors }"
                                    v-if="editedDiscountItem.DiscountType === 2"
                                  >
                                    <v-text-field
                                      v-model="editedDiscountItem.GiveMoney"
                                      v-if="editedDiscountItem.DiscountType === 2"
                                      @keyup.enter="focusNextInput"
                                      :error-messages="errors[0]"
                                    >
                                      <template v-slot:label>
                                        <span class="red--text">
                                          <strong>* </strong>
                                        </span>
                                        {{ $t('discount_item_give_money') }}
                                      </template>
                                    </v-text-field>
                                  </ValidationProvider>
                                  <ValidationProvider
                                    :name="$t('discount_item_give_percent')"
                                    rules="required"
                                    v-slot="{ errors }"
                                    v-if="editedDiscountItem.DiscountType === 3"
                                  >
                                    <v-text-field
                                      v-model="editedDiscountItem.GivePercent"
                                      v-if="editedDiscountItem.DiscountType === 3"
                                      @keyup.enter="focusNextInput"
                                      :error-messages="errors[0]"
                                    >
                                      <template v-slot:label>
                                        <span class="red--text">
                                          <strong>* </strong>
                                        </span>
                                        {{ $t('discount_item_give_percent') }}
                                      </template>
                                    </v-text-field>
                                  </ValidationProvider>
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="12" sm="6" md="4" xl="3" lg="3"
                                  ><v-checkbox
                                    v-model="editedDiscountItem.IsRepeat"
                                    :label="$t('discount_item_repeat')"
                                    @change="lockUseDiscountType"
                                  ></v-checkbox>
                                </v-col>
                                <v-col cols="12" sm="6" md="4" xl="3" lg="3">
                                  <ValidationProvider
                                    :name="$t('discount_item_repeat_warranty_period')"
                                    rules="required"
                                    v-slot="{ errors }"
                                    v-if="editedDiscountItem.IsRepeat"
                                  >
                                    <v-text-field
                                      v-model="editedDiscountItem.RepeatWarrantyPeriod"
                                      v-if="editedDiscountItem.IsRepeat"
                                      @keyup.enter="focusNextInput"
                                      :error-messages="errors[0]"
                                    >
                                      <template v-slot:label>
                                        <span class="red--text">
                                          <strong>* </strong>
                                        </span>
                                        {{ $t('discount_item_repeat_warranty_period') }}
                                      </template>
                                    </v-text-field>
                                  </ValidationProvider>
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-textarea
                                  outlined
                                  :label="$t('quotation_item_remark')"
                                  v-model="editedDiscountItem.Remark"
                                ></v-textarea>
                              </v-row>
                            </ValidationObserver>
                          </v-container>
                        </v-card-text>
                        <v-data-table
                          :headers="groupTitle"
                          hide-default-header
                          hide-default-footer
                          :items="editedDiscountItem.SortList"
                          single-expand
                          item-key="Sort"
                          show-expand
                          class="elevation-1 mx-5"
                          :no-data-text="$t('common_no_data')"
                          :no-results-text="$t('common_no_results')"
                        >
                          <template v-slot:[`item.Sort`]="{ item }">
                            {{ $t('discount_item_title') }} {{ item.Sort }}
                            {{ $t('discount_item_title_second') }}
                          </template>
                          <template v-slot:expanded-item="{ headers, item }">
                            <td :colspan="headers.length">
                              <v-data-table
                                :headers="noticeDataTableHeaders"
                                :items="item.Notices"
                                class="ma-5"
                                :loading-text="$t('common_get_data')"
                                :no-data-text="$t('common_no_data')"
                                :no-results-text="$t('common_no_results')"
                                :footer-props="{
                                  'items-per-page-options': [30, 50, 100],
                                  'items-per-page-text': $t('item_rows_per_page'),
                                }"
                                :header-props="headerProps"
                              >
                                <template v-slot:[`item.StartTime`]="{ item }">
                                  {{ convertToDateTimeFormat(item.StartTime) }}
                                </template>
                                <template v-slot:[`item.EndTime`]="{ item }">
                                  {{ convertToDateTimeFormat(item.EndTime) }}
                                </template>
                                <template v-slot:[`item.SettleStartTime`]="{ item }">
                                  {{ convertToDateTimeFormat(item.SettleStartTime) }}
                                </template>
                                <template v-slot:[`item.SettleEndTime`]="{ item }">
                                  {{ convertToDateTimeFormat(item.SettleEndTime) }}
                                </template>
                                <template v-slot:[`item.DiscountTime`]="{ item }">
                                  {{ convertToDateTimeFormat(item.DiscountTime) }}
                                </template>
                              </v-data-table>
                            </td>
                          </template>
                        </v-data-table>
                        <v-card-actions>
                          <v-spacer></v-spacer>
                          <v-btn color="red darken-1" text @click="closeEditDiscountItem">
                            {{ $t('common_cancel') }}
                          </v-btn>
                          <v-btn color="green darken-1" text @click="addDiscountItem">
                            {{ $t('common_confirm') }}
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                    <delete-dialog
                      :isAllTextFieldDisable="isAllTextFieldDisable"
                      :isLoadingSaveBtn="isLoadingSaveBtn"
                      :isShowDeleteDialog="isShowDeleteDiscountItemDialog"
                      :deleteMessage="'bom_item_delete_dialog_message'"
                      @closeDelete="closeDeleteDiscountItem"
                      @confirmDelete="confirmDeleteDiscountItem"
                    />
                  </v-toolbar>
                </template>
                <template v-slot:[`item.UseDiscountType`]="{ item }">
                  {{ turnUseDiscountType(item.UseDiscountType) }}
                </template>
                <template v-slot:[`item.DiscountType`]="{ item }">
                  {{ turnDiscountType(item.DiscountType) }}
                </template>
                <template v-slot:[`item.Edit`]="{ item }">
                  <v-btn
                    icon
                    @click="isShowEditDiscountItemDialogBtn(item)"
                    :disabled="isAllTextFieldDisable"
                  >
                    <v-icon small>
                      mdi-pencil
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    @click="isShowDeleteDiscountItemDialogBtn(item)"
                    :disabled="isAllTextFieldDisable"
                  >
                    <v-icon small>
                      mdi-delete
                    </v-icon>
                  </v-btn>
                </template>
              </v-data-table>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="red darken-1"
                  text
                  @click="closeEditDiscountDialog"
                  :disabled="isAllTextFieldDisable"
                >
                  {{ $t('common_cancel') }}
                </v-btn>
                <v-btn
                  color="green darken-1"
                  text
                  @click="saveDiscount"
                  :loading="isLoadingSaveBtn"
                >
                  {{ $t('common_save') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <delete-dialog
            :isAllTextFieldDisable="isAllTextFieldDisable"
            :isLoadingSaveBtn="isLoadingSaveBtn"
            :isShowDeleteDialog="isShowDeleteDiscountDialog"
            :deleteMessage="'bom_delete_dialog_message'"
            @closeDelete="closeDeleteDiscount"
            @confirmDelete="confirmDeleteDiscount"
          />
        </v-toolbar>
      </template>
      <template v-slot:[`item.Edit`]="{ item }">
        <v-btn icon @click="isShowEditDiscountDialogBtn(item)">
          <v-icon small>
            mdi-pencil
          </v-icon>
        </v-btn>
        <v-btn icon @click="isShowDeleteDialogBtn(item)">
          <v-icon small>
            mdi-delete
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import CustomerAutocomplete from '@/components/CustomerAutocomplete.vue'
import DeleteDialog from '@/components/DeleteDialog.vue'
import { HttpDiscount } from '@/js/http-Manager'
import { DiscountType } from '@/js/DiscountType'
import { UseDiscountType } from '@/js/UseDiscountType'
import DatePickerRequired from '@/components/DatePickerRequired.vue'

export default {
  components: { CustomerAutocomplete, DeleteDialog, DatePickerRequired },
  name: 'Discount',

  data: () => ({
    discountList: [],
    isLoadingDiscount: false,
    isShowEditDiscountDialog: false,
    editedDiscount: {
      CustomerId: '',
      CustomerNumber: '',
      CustomerName: '',
      CustomerFullName: '',
      LicensePlate: '',
      MonthTotal: '',
      WarrantyPeriod: '',
      DiscountItems: []
    },
    defaultDiscount: {
      CustomerId: '',
      CustomerNumber: '',
      CustomerName: '',
      CustomerFullName: '',
      LicensePlate: '',
      MonthTotal: 0,
      WarrantyPeriod: 0,
      DiscountItems: []
    },
    editedDiscountIndex: -1,
    isAllTextFieldDisable: false,
    isLoadingSaveBtn: false,
    isShowDeleteDiscountDialog: false,
    isShowEditDiscountItemDialog: false,
    isShowDeleteDiscountItemDialog: false,
    editedDiscountItemIndex: -1,
    searchDiscountItem: '',
    editedDiscountItem: {
      DiscountType: 0,
      IsRepeat: false,
      RepeatWarrantyPeriod: 0,
      RepeatDay: 0,
      GiveWarrantyPeriod: 0,
      GiveMoney: 0,
      GivePercent: 0,
      UseDiscountType: 0,
      UseWarrantyPeriods: [],
      UseDays: [],
      UseWarrantyPeriod: '',
      UseDay: '',
      StartTime: 0,
      Remark: '',
      IsUse: false,
      Month: 0,
      SortList: []
    },
    defaultDiscountItem: {
      DiscountType: 0,
      IsRepeat: false,
      RepeatWarrantyPeriod: 0,
      RepeatDay: 0,
      GiveWarrantyPeriod: 0,
      GiveMoney: 0,
      GivePercent: 0,
      UseDiscountType: 0,
      UseWarrantyPeriods: [],
      UseDays: [],
      StartTime: 0,
      Remark: '',
      IsUse: false,
      Month: 0,
      SortList: []
    },
    allDiscountType: [],
    allDiscountItemType: [],
    lock: false,
    showList: false,
    tempUseWarrantyPeriods: []
  }),
  computed: {
    discountDataTableHeaders () {
      return [
        { text: this.$t('quotation_customer_number'), value: 'CustomerNumber' },
        { text: this.$t('quotation_shortname'), value: 'CustomerName' },
        { text: this.$t('quotation_full_name'), value: 'CustomerFullName' },
        { text: this.$t('quotation_license_plate'), value: 'LicensePlate' },
        { text: this.$t('discount_month_total'), value: 'MonthTotal' },
        { text: this.$t('quotation_item_rent_warranty_period'), value: 'WarrantyPeriod' },
        { text: this.$t('common_edit'), value: 'Edit', sortable: false }
      ]
    },
    headerProps () {
      return { sortByText: this.$t('installation_acceptance_header_sort') }
    },
    discountDialogTitle () {
      return this.editedDiscountIndex === -1 ? this.$t('common_new') : this.$t('common_edit')
    },
    discountItemDataTableHeaders () {
      return [
        { text: this.$t('month_item_status'), value: 'IsUse' },
        { text: this.$t('discount_item_type'), value: 'DiscountType' },
        { text: this.$t('discount_item_repeat'), value: 'IsRepeat' },
        { text: this.$t('discount_item_use_type'), value: 'UseDiscountType' },
        { text: this.$t('common_edit'), value: 'Edit', sortable: false }
      ]
    },
    discountItemDialogTitle () {
      return this.editedDiscountItemIndex === -1 ? this.$t('common_new') : this.$t('common_edit')
    },
    computeStartTime () {
      return this.convertToDateTimeFormat(this.editedDiscountItem.StartTime)
    },
    allMonth () {
      let array = []
      if (this.editedDiscountItem.IsRepeat) {
        let month =
          this.editedDiscountItem.RepeatWarrantyPeriod * 1 +
          this.editedDiscountItem.GiveWarrantyPeriod * 1
        for (let index = 1; index <= month; index++) {
          array.push(index)
        }
        return array
      } else {
        let month =
          this.editedDiscount.WarrantyPeriod * 1 + this.editedDiscountItem.GiveWarrantyPeriod * 1
        for (let index = 1; index <= month; index++) {
          array.push(index)
        }
        return array
      }
    },
    noticeDataTableHeaders () {
      return [
        { text: '起始日期', value: 'StartTime' },
        { text: '扣除日期', value: 'DiscountTime' },
        { text: '迄止日期', value: 'EndTime' },
        { text: '結帳起始日期', value: 'SettleStartTime' },
        { text: '結帳迄止日期', value: 'SettleEndTime' },
        { text: '天數', value: 'Day' },
        { text: '銷貨單單別', value: 'SaleType' },
        { text: '銷貨單單號', value: 'SaleNumber' },
        { text: '扣除額', value: 'DiscountMoney' },
        { text: '已使用額度', value: 'UseDiscountMoney' }
      ]
    },
    groupTitle () {
      return [
        { text: '', value: 'Sort' },
        { text: '', align: 'end', value: 'data-table-expand' }
      ]
    }
  },
  async mounted () {
    await this.getDiscount()
    await this.getAllDiscountType()
    await this.getAllDiscountItemType()
  },
  methods: {
    setKeyDownAutocomplete () {
      ///  儲存keydown之前的Roles
      this.tempUseWarrantyPeriods = this.editedDiscountItem.UseWarrantyPeriods
    },
    setKeyUpAutocomplete (event) {
      this.$refs.useWarrantyPeriodsAutocomplete.isMenuActive = false
      this.$refs.useWarrantyPeriodsAutocomplete.isFocused = false
      ///  塞回keydown之前的Roles  給KeyUp的Model editedUser.Roles
      this.editedDiscountItem.UseWarrantyPeriods = this.tempUseWarrantyPeriods
      this.focusNextInput(event)
    },
    setUseDiscountTypeAutocomplete (event) {
      this.$refs.useDiscountTypeAutocomplete.isMenuActive = false
      this.$refs.useDiscountTypeAutocomplete.isFocused = false
      this.focusNextInput(event)
    },
    setDiscountTypeAutocomplete (event) {
      this.$refs.discountTypeAutocomplete.isMenuActive = false
      this.$refs.discountTypeAutocomplete.isFocused = false
      this.focusNextInput(event)
    },
    focusNextInput (event) {
      let form = document.getElementsByTagName('input')
      let targets = []
      for (let i = 0; i < form.length; i++) {
        if (form[i].type !== 'hidden' && !form[i].disabled && form[i].type !== 'checkbox') {
          targets.push(form[i])
        }
      }
      let index = Array.prototype.indexOf.call(targets, event.target)
      if (index !== -1) {
        let next = index + 1
        if (next < targets.length) {
          targets[next].focus()
          targets[next].select()
        }
      }
    },
    turnUseDiscountType (val) {
      return UseDiscountType.UseDiscountTypeName(val)
    },
    turnDiscountType (val) {
      return DiscountType.DiscountTypeName(val)
    },
    lockUseDiscountType () {
      if (this.editedDiscountItem.DiscountType === 1 && this.editedDiscountItem.IsRepeat) {
        this.editedDiscountItem.UseDiscountType = 1
        this.lock = true
      } else {
        this.lock = false
      }
    },
    convertToDateTimeFormat (timestamp) {
      if (timestamp === 0) {
        return '-'
      } else {
        return this.$moment(new Date(timestamp)).format('YYYY-MM-DD')
      }
    },
    setStartTime (val) {
      this.editedDiscountItem.StartTime = val * 1
    },
    async getAllDiscountType () {
      let response = await HttpDiscount.GetDiscountType()
      this.allDiscountType = response.status === 200 ? response.data.Data : []
    },
    async getAllDiscountItemType () {
      let response = await HttpDiscount.GetDiscountItemType()
      this.allDiscountItemType = response.status === 200 ? response.data.Data : []
    },
    async getDiscount () {
      this.isLoadingDiscount = true
      let response = await HttpDiscount.GetDiscount()
      this.discountList = response.status === 200 ? response.data.Data : []
      this.isLoadingDiscount = false
    },
    async setCustomer (val) {
      this.editedDiscount.CustomerId = val.Id
      this.editedDiscount.CustomerNumber = val.Number
      this.editedDiscount.CustomerName = val.ShortName
      this.editedDiscount.CustomerFullName = val.FullName
      this.editedDiscount = JSON.parse(JSON.stringify(this.editedDiscount))
    },
    isShowEditDiscountDialogBtn (item) {
      this.editedDiscountIndex = this.discountList.indexOf(item)
      this.editedDiscount = JSON.parse(JSON.stringify(item))
      this.isShowEditDiscountDialog = true
    },
    closeEditDiscountDialog () {
      this.isShowEditDiscountDialog = false
      this.$nextTick(() => {
        this.editedDiscountIndex = -1
        this.editedDiscount = JSON.parse(JSON.stringify(this.defaultDiscount))
      })
    },
    async saveDiscount () {
      const isValid = await this.$refs.dialogDiscount.validate()
      if (isValid) {
        this.isLoadingSaveBtn = true
        this.isAllTextFieldDisable = true
        if (this.editedDiscountIndex === -1) {
          await HttpDiscount.AddDiscount(this.editedDiscount)
        } else {
          await HttpDiscount.UpdateDiscount(this.editedDiscount)
        }
        this.isLoadingSaveBtn = false
        this.isAllTextFieldDisable = false
        await this.getDiscount()
        this.closeEditDiscountDialog()
      }
    },
    isShowDeleteDialogBtn (item) {
      this.editedDiscountIndex = this.discountList.indexOf(item)
      this.isShowDeleteDiscountDialog = true
    },
    closeDeleteDiscount () {
      this.editedDiscountIndex = -1
      this.isShowDeleteDiscountDialog = false
    },
    async confirmDeleteDiscount () {
      this.isLoadingSaveBtn = true
      this.isAllTextFieldDisable = true
      await HttpDiscount.UpdateDiscountIsDelete(this.discountList[this.editedDiscountIndex].Id)
      this.isLoadingSaveBtn = false
      this.isAllTextFieldDisable = false
      await this.getDiscount()
      this.closeDeleteDiscount()
    },
    isShowEditDiscountItemDialogBtn (item) {
      this.editedDiscountItemIndex = this.editedDiscount.DiscountItems.indexOf(item)
      this.editedDiscountItem = JSON.parse(JSON.stringify(item))
      this.isShowEditDiscountItemDialog = true
    },
    closeEditDiscountItem () {
      this.isShowEditDiscountItemDialog = false
      this.$nextTick(() => {
        this.editedDiscountItemIndex = -1
        this.editedDiscountItem = JSON.parse(JSON.stringify(this.defaultDiscountItem))
      })
    },
    async addDiscountItem () {
      const isValid = await this.$refs.discountItem.validate()
      if (isValid) {
        if (this.editedDiscountItemIndex === -1) {
          this.editedDiscount.DiscountItems.push(this.editedDiscountItem)
        } else {
          this.editedDiscount.DiscountItems.splice(
            this.editedDiscountItemIndex,
            1,
            this.editedDiscountItem
          )
        }
        this.closeEditDiscountItem()
      }
    },
    isShowDeleteDiscountItemDialogBtn (item) {
      this.editedDiscountItemIndex = this.editedDiscount.DiscountItems.indexOf(item)
      this.isShowDeleteDiscountItemDialog = true
    },
    closeDeleteDiscountItem () {
      this.isShowDeleteDiscountItemDialog = false
      this.$nextTick(() => {
        this.editedDiscountItemIndex = -1
      })
    },
    confirmDeleteDiscountItem () {
      this.editedDiscount.DiscountItems.splice(this.editedDiscountItemIndex, 1)
      this.closeDeleteDiscountItem()
    }
  }
}
</script>
